'use client';

import React from 'react';
import { Send, X, Tv2, Clapperboard } from 'lucide-react';
import type { MediaItem } from '@/lib/types';

interface Props {
  selectedCount: number;
  selectedItems: MediaItem[];
  onDispatch: () => void;
  onClear: () => void;
}

export default function DispatchBar({ selectedCount, selectedItems, onDispatch, onClear }: Props) {
  const seriesCount = selectedItems.filter((i) => i.mediaType === 'series').length;
  const moviesCount = selectedItems.filter((i) => i.mediaType === 'movie').length;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 border-t border-border dispatch-bar-enter"
      style={{ background: 'var(--card)' }}
    >
      <div className="max-w-screen-2xl mx-auto px-6 xl:px-8 2xl:px-10 py-3 flex items-center justify-between gap-4">
        {/* Selection summary */}
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-primary-foreground font-mono-data">
              {selectedCount}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
            </p>
            <div className="flex items-center gap-3 mt-0.5">
              {seriesCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Tv2 size={11} className="text-sonarr" />
                  <span className="font-mono-data">{seriesCount}</span> series
                </span>
              )}
              {moviesCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clapperboard size={11} className="text-radarr" />
                  <span className="font-mono-data">{moviesCount}</span> movies
                </span>
              )}
            </div>
          </div>

          {/* Mini preview of selected titles */}
          <div className="hidden lg:flex items-center gap-1.5 overflow-hidden max-w-md">
            {selectedItems.slice(0, 4).map((item) => (
              <span
                key={`bar-item-${item.id}`}
                className="text-2xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground truncate max-w-[120px]"
              >
                {item.title}
              </span>
            ))}
            {selectedItems.length > 4 && (
              <span className="text-2xs text-muted-foreground">
                +{selectedItems.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onClear}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium btn-ghost"
          >
            <X size={15} />
            Clear
          </button>
          <button
            onClick={onDispatch}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold btn-primary pulse-glow"
          >
            <Send size={15} />
            Dispatch {selectedCount} to Webhook
          </button>
        </div>
      </div>
    </div>
  );
}